import { useState, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9qshsnnahb7382ovevg';

const faqItems = [
  {
    q: '黑料网在线是什么样的平台？',
    a: '黑料网在线是一个独立的网络内容聚合平台，致力于收集和整理来自互联网的各类爆料信息，涵盖娱乐圈、网红主播、社会事件、科技圈、体育圈等多个领域的最新资讯和独家内幕。我们坚持客观、真实的原则，为用户提供多元化的信息视角。',
  },
  {
    q: '黑料网在线的内容来源是什么？',
    a: '我们的内容来源于公开网络信息、网友投稿、合作媒体以及独立调查团队的一手报道。所有内容均经过编辑团队的多方核实和筛选，确保信息的真实性和可靠性。同时，我们严格遵守相关法律法规，保护个人隐私和合法权益。',
  },
  {
    q: '如何向黑料网在线投稿？',
    a: '您可以通过本页面下方公布的联系方式向我们提交爆料线索。我们设有专门的线索审核团队，对每一条投稿进行认真评估。对于经核实有价值的爆料内容，我们会联系投稿人进一步沟通，并提供相应稿酬。',
  },
  {
    q: '黑料网在线如何保证内容真实性？',
    a: '我们建立了严格的"三审三校"制度。每篇稿件在发布前，必须经过编辑初审、主编复审、总编终审三道审核流程，以及三次事实核查校对。对于涉及重大公共利益的内容，我们还会引入外部专家和法律顾问参与评估。',
  },
  {
    q: '如果在黑料网在线看到不实内容怎么办？',
    a: '我们设有专门的纠错和投诉渠道。如果您认为我们的报道存在事实性错误或不当内容，请通过页面下方公布的联系邮箱与我们取得联系。我们承诺在收到投诉后24小时内启动核查程序，如确认有误，将及时更正或撤回。',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map(item => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '黑料网在线',
  url: 'https://heiliao.online',
  description: '黑料网在线为您提供最新、最全的网络爆料内容，实时聚合热点黑料资讯，涵盖娱乐圈、网红主播、社会事件等多领域独家内幕。',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@heiliao.online',
    availableLanguage: 'Chinese',
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于黑料网在线',
  description: '了解黑料网在线的品牌故事、口碑评价和联系方式。',
  url: 'https://heiliao.online/about',
  publisher: {
    '@type': 'Organization',
    name: '黑料网在线',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: '首页',
      item: 'https://heiliao.online',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: '关于我们',
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const contactFormRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      return;
    }
    try {
      const fd = new FormData(form);
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;
        setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <Breadcrumb />

      <main>
        {/* Page Header */}
        <section className="py-10 md:py-14 bg-background-50 border-b border-background-200/70">
          <div className="container-custom text-center max-w-2xl mx-auto">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
              关于黑料网在线
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              真相从不缺席，我们一直在路上
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section id="brand" className="py-10 md:py-14 bg-background-50">
          <div className="container-custom">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap mb-3 inline-block">
                  品牌介绍
                </span>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                  黑料网在线 — 探索真相的聚集地
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    黑料网在线成立于2024年初，是国内领先的网络爆料内容聚合平台。我们以"让真相不再沉默"为使命，致力于为广大网民提供最新、最全、最真实的热点资讯和独家内幕。
                  </p>
                  <p>
                    我们的团队由资深媒体人、专业调查记者和技术开发人员组成，分布在北京、上海、深圳三地。团队核心成员均拥有5年以上主流媒体工作经验，对信息真伪的辨别和深度调查具有丰富的实战经验。
                  </p>
                  <p>
                    成立至今，黑料网在线已累计发布独家深度报道超过500篇，月活跃用户突破200万。我们的报道多次被主流媒体引用，并成功推动了多起社会热点事件的公开透明化进程。
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                    <div className="text-center p-4 bg-background-100 rounded-lg">
                      <div className="font-heading font-bold text-xl md:text-2xl text-primary-500">500+</div>
                      <div className="text-xs text-foreground-500 mt-1">独家报道</div>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-lg">
                      <div className="font-heading font-bold text-xl md:text-2xl text-primary-500">200万+</div>
                      <div className="text-xs text-foreground-500 mt-1">月活用户</div>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-lg">
                      <div className="font-heading font-bold text-xl md:text-2xl text-primary-500">30+</div>
                      <div className="text-xs text-foreground-500 mt-1">专业编辑</div>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-lg">
                      <div className="font-heading font-bold text-xl md:text-2xl text-primary-500">99.7%</div>
                      <div className="text-xs text-foreground-500 mt-1">内容准确率</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20newsroom%20with%20journalists%20working%20at%20desks%2C%20warm%20ambient%20lighting%2C%20professional%20editorial%20environment%2C%20clean%20minimalist%20design%2C%20wooden%20desks%2C%20large%20windows%20with%20natural%20light%2C%20documentary%20photography%20style%2C%20authentic%20workplace%20scene&width=600&height=500&seq=about-brand&orientation=landscape"
                  alt="黑料网在线办公室"
                  title="黑料网在线 - 编辑团队"
                  className="w-full rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-10 md:py-14 bg-background-100">
          <div className="container-custom">
            <div className="text-center mb-8 md:mb-10">
              <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap mb-3 inline-block">
                用户口碑
              </span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">
                他们都在用黑料网在线
              </h2>
              <p className="text-sm text-foreground-500">来自真实用户的声音</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  name: '老王',
                  role: '资深网民',
                  rating: 5,
                  text: '每天必刷黑料网在线，信息更新速度真的快，很多爆料比其他平台早了整整一天！内容质量也高，能看出来是有真实调查在里面的。',
                },
                {
                  name: '小雨',
                  role: '自媒体从业者',
                  rating: 5,
                  text: '作为自媒体人，黑料网在线是我的重要信息来源之一。深度报道做得特别扎实，引用来源清晰，做二次创作的时候心里有底。强烈推荐！',
                },
                {
                  name: 'Alex',
                  role: '互联网从业者',
                  rating: 4,
                  text: '关注黑料网半年多了，最喜欢科技圈和网红主播板块。有些内幕消息真的是别处看不到的，希望继续保持这种敢说真话的风格。',
                },
                {
                  name: '大明',
                  role: '媒体人',
                  rating: 5,
                  text: '在传统媒体干了十年，说实话黑料网在线的一些调查报道水准已经超过了很多专业媒体机构。三审三校制度保证了内容质量，很专业。',
                },
                {
                  name: '小陈',
                  role: '在校大学生',
                  rating: 5,
                  text: '室友推荐的，一开始觉得就是八卦，后来发现很多社会事件的深度报道做得非常好。现在每天打开看看有没有新内容已经成了习惯。',
                },
                {
                  name: '李姐',
                  role: '企业高管',
                  rating: 4,
                  text: '商业嗅觉比较敏锐的朋友推荐我关注的。黑料网在线的科技圈和财经报道给了我很多启发，对行业趋势的判断也帮到了我的工作。',
                },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-600">{review.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-10 md:py-14 bg-background-50">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap mb-3 inline-block">
                  联系方式
                </span>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">欢迎爆料投稿、商务合作、意见建议</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
                <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1">爆料投稿</h4>
                  <p className="text-xs text-foreground-500">tips@heiliao.online</p>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-briefcase-line text-lg text-accent-600"></i>
                  </div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1">商务合作</h4>
                  <p className="text-xs text-foreground-500">business@heiliao.online</p>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-customer-service-line text-lg text-secondary-600"></i>
                  </div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1">客服支持</h4>
                  <p className="text-xs text-foreground-500">support@heiliao.online</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-4">在线留言</h3>
                <form
                  ref={contactFormRef}
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="name">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 text-foreground-950 border border-background-200/70 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="email">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 text-foreground-950 border border-background-200/70 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入邮箱"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="message">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 text-foreground-950 border border-background-200/70 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入留言内容（最多500字）"
                      maxLength={500}
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="ab-form-hp"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md transition-colors whitespace-nowrap cursor-pointer self-start"
                  >
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600">留言已成功提交！感谢你的反馈。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="container-custom max-w-3xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap mb-3 inline-block">
                常见问题
              </span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">
                你可能会问
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{item.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}